import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { type ClosedLostDetails } from '@/models/salesLead'
import {
  type ClosedLostDraft,
  ClosedLostReasonFields,
  EMPTY_CLOSED_LOST_DRAFT,
  isClosedLostDraftComplete,
} from './ClosedLostReasonFields'

interface ClosedLostReasonDialogProps {
  open: boolean
  saving?: boolean
  onCancel: () => void
  onConfirm: (details: ClosedLostDetails) => void
}

/** Asks why a lead was lost before it moves to Closed/Lost. */
export function ClosedLostReasonDialog({ open, saving, onCancel, onConfirm }: ClosedLostReasonDialogProps) {
  const [draft, setDraft] = useState<ClosedLostDraft>(EMPTY_CLOSED_LOST_DRAFT)

  // A fresh pick every time it opens, rather than the last lead's reason.
  useEffect(() => {
    if (open) setDraft(EMPTY_CLOSED_LOST_DRAFT)
  }, [open])

  function confirm() {
    if (draft.reason === null || !isClosedLostDraftComplete(draft)) return
    onConfirm({ closedLostReason: draft.reason, notes: draft.notes.trim() })
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to Closed/Lost</DialogTitle>
          <DialogDescription>Pick why this lead was lost.</DialogDescription>
        </DialogHeader>
        <ClosedLostReasonFields idPrefix="dialog" draft={draft} onChange={setDraft} />
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={!isClosedLostDraftComplete(draft)}
            loading={saving ? 'Saving…' : undefined}
          >
            Move to Closed/Lost
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
